/**
 * scenarioSourceLabel.ts
 * 2026-07-10 scenario-naming fix — human-readable source identity for a
 * scenario, shared by the dashboard list and the locked-plan dialog.
 *
 * A plan for the quote "Terrane" used to render as "Quote (…9065)" — the
 * quote's title and client were invisible, so planners couldn't find their
 * plan. The backend now JOINs the source identity onto scenario reads
 * (ScenarioWithSource); this helper turns those fields into display lines:
 *
 *   Quote:     primary "Terrane — Q-2026-9065",   secondary "TestNewClient"
 *   WorkOrder: primary "WO-2026-045 — Sample Prep", secondary "Mine Expansion"
 *
 * Falls back to the old GUID-tail label when the joined fields are absent
 * (stale backend during rollout, or the source row was deleted).
 */

import type { ScenarioSourceType } from '../../../../services/scheduleScenario';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Structural view of the source-identity fields (Scenario / ScenarioListItem). */
export interface ScenarioSourceInfo {
  SourceType: ScenarioSourceType | null;
  QuoteKey?: string | null;
  WorkOrderKey?: string | null;
  QuoteNumber?: string | null;
  QuoteTitle?: string | null;
  ClientName?: string | null;
  WorkOrderId?: string | null;
  WorkOrderName?: string | null;
  ProjectName?: string | null;
}

export interface SourceLines {
  primary: string;
  secondary: string | null;
}

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

export function sourceLines(item: ScenarioSourceInfo): SourceLines {
  if (!item.SourceType) return { primary: '—', secondary: null };

  if (item.SourceType === 'Quote' && item.QuoteNumber) {
    const title = item.QuoteTitle?.trim() || null;
    return {
      primary: [title ?? item.ClientName, item.QuoteNumber].filter(Boolean).join(' — '),
      secondary: title ? (item.ClientName ?? null) : null,
    };
  }

  if (item.SourceType === 'WorkOrder' && item.WorkOrderId) {
    const woName = item.WorkOrderName?.trim() || null;
    return {
      primary: [item.WorkOrderId, woName ?? item.ProjectName].filter(Boolean).join(' — '),
      secondary: woName ? (item.ProjectName ?? null) : null,
    };
  }

  // Fallback: pre-fix payload shape (no joined source fields).
  const id = item.SourceType === 'Quote'
    ? (item.QuoteKey ? `…${item.QuoteKey.slice(-6)}` : '')
    : (item.WorkOrderKey ? `…${item.WorkOrderKey.slice(-6)}` : '');
  return { primary: id ? `${item.SourceType} (${id})` : item.SourceType, secondary: null };
}
